import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Loader2, FileText, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function ApplyDialog({ open, onOpenChange, job }) {
  const { user } = useAuth();
  const inputRef = useRef();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    pitch: "",
  });
  const [resumePath, setResumePath] = useState("");
  const [resumeName, setResumeName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadResume = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResumePath(data.path);
      setResumeName(file.name);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to upload resume");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.pitch.trim() || form.pitch.trim().length < 10) {
      return toast.error("Name, email, and a short pitch (10+ chars) are required");
    }
    setSubmitting(true);
    try {
      await api.post(`/jobs/${job.id}/apply`, { ...form, resume_path: resumePath });
      toast.success("Application sent — the employer will be in touch");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1E3A2F]">Apply to {job?.title}</DialogTitle>
          <DialogDescription>Your details go straight to the employer's inbox. Keep it warm and specific.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Your name</Label>
              <Input data-testid="apply-name" value={form.name} onChange={(e) => set("name", e.target.value)} required />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="apply-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required />
            </div>
          </div>
          <div>
            <Label>Phone (optional)</Label>
            <Input data-testid="apply-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+1 206 555 0142" />
          </div>
          <div>
            <Label>Short pitch — why you're a fit</Label>
            <Textarea data-testid="apply-pitch" rows={5} value={form.pitch} onChange={(e) => set("pitch", e.target.value)} placeholder="Tell the employer about your experience, availability and what makes you a great match…" />
          </div>
          <div>
            <Label>Resume (optional, PDF or image, up to 5MB)</Label>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,image/*"
              className="hidden"
              data-testid="apply-resume-input"
              onChange={(e) => e.target.files?.[0] && uploadResume(e.target.files[0])}
            />
            {resumePath ? (
              <div className="mt-2 flex items-center justify-between rounded-xl border border-[#E2DED0] px-3 py-2 bg-[#F7F4EB]">
                <div className="inline-flex items-center gap-2 text-sm text-[#1E3A2F] truncate">
                  <FileText className="w-4 h-4" /> {resumeName || "Resume uploaded"}
                </div>
                <button
                  type="button"
                  data-testid="apply-resume-remove"
                  onClick={() => { setResumePath(""); setResumeName(""); }}
                  className="w-7 h-7 rounded-full grid place-items-center hover:bg-white"
                >
                  <X className="w-4 h-4 text-[#C05A3D]" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                data-testid="apply-resume-trigger"
                disabled={uploading}
                onClick={() => inputRef.current?.click()}
                className="mt-2 w-full border border-dashed border-[#C05A3D]/40 rounded-xl p-4 text-center hover:bg-[#C05A3D]/5 transition disabled:opacity-50"
              >
                {uploading ? (
                  <span className="inline-flex items-center gap-2 text-[#1E3A2F]"><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</span>
                ) : (
                  <span className="inline-flex items-center gap-2 text-[#1E3A2F]"><Upload className="w-4 h-4" /> Upload resume</span>
                )}
              </button>
            )}
          </div>
          <Button type="submit" data-testid="apply-submit" disabled={submitting} className="abm-btn-primary rounded-full w-full h-11">
            {submitting ? "Sending…" : "Send application"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
